// src/utils/energyRoomDrawing.js
// Drawing utilities for the Energy Room editor.
// Panel position (x, y) is the CENTER of the panel.
// Panel rotation is in radians around the center.
// Panel sizes are in cm and converted via: worldUnits = cm / drawingScale
import * as THREE from 'three';

const COLORS = {
  room: 0xcccccc,
  panel: 0x4fc3f7,
  panelSelected: 0x81d4fa,
  panelIntersect: 0xff4444,
  dimension: 0x4fc3f7,
  dimText: '#4fc3f7',
  compartmentText: '#4fc3f7',
  compartmentTextIntersect: '#ff4444',
};

// ─── Local-coord line (z=0, used inside rotated groups) ──

function createLineLocal(x1, y1, x2, y2, color = COLORS.room) {
  const pts = [new THREE.Vector3(x1, y1, 0), new THREE.Vector3(x2, y2, 0)];
  const geo = new THREE.BufferGeometry().setFromPoints(pts);
  return new THREE.Line(geo, new THREE.LineBasicMaterial({ color }));
}

// ─── World-coord line (for clipped room segments) ──────

function createLineWorld(x1, y1, x2, y2, color = COLORS.room) {
  const pts = [new THREE.Vector3(x1, y1, 0), new THREE.Vector3(x2, y2, 0)];
  const geo = new THREE.BufferGeometry().setFromPoints(pts);
  return new THREE.Line(geo, new THREE.LineBasicMaterial({ color }));
}

// ─── Text sprite (positioned in local coords inside group) ─────────

function createDimensionLabel(text, wx, wy, fontSize, color = COLORS.dimText) {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  const pxScale = 4;
  const pxSize = fontSize * pxScale;
  ctx.font = `${pxSize}px Arial`;
  const tw = ctx.measureText(text).width;

  canvas.width = tw + 4 * pxScale;
  canvas.height = pxSize + 4 * pxScale;
  ctx.font = `${pxSize}px Arial`;
  ctx.fillStyle = color;
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'center';
  ctx.fillText(text, canvas.width / 2, canvas.height / 2);

  const texture = new THREE.CanvasTexture(canvas);
  texture.needsUpdate = true;
  const sprite = new THREE.Sprite(
    new THREE.SpriteMaterial({ map: texture, transparent: true, depthTest: false })
  );
  sprite.position.set(wx, wy, 0.1);
  const worldW = canvas.width / pxScale;
  const worldH = canvas.height / pxScale;
  sprite.scale.set(worldW, worldH, 1);
  return sprite;
}

// ─── Multi-line text sprite (all lines on one canvas — rotation-safe) ──

function createMultiLineTextSprite(lines, wx, wy, fontSize, color = COLORS.dimText) {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  const pxScale = 4;
  const pxSize = fontSize * pxScale;
  const lineHeight = pxSize * 1.4;

  ctx.font = `${pxSize}px Arial`;
  let maxWidth = 0;
  for (const line of lines) {
    const tw = ctx.measureText(line).width;
    if (tw > maxWidth) maxWidth = tw;
  }

  canvas.width = maxWidth + 4 * pxScale;
  canvas.height = lineHeight * lines.length + 4 * pxScale;

  ctx.font = `${pxSize}px Arial`;
  ctx.fillStyle = color;
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'center';

  for (let i = 0; i < lines.length; i++) {
    const y = lineHeight * (i + 0.5) + 2 * pxScale;
    ctx.fillText(lines[i], canvas.width / 2, y);
  }

  const texture = new THREE.CanvasTexture(canvas);
  texture.needsUpdate = true;
  const sprite = new THREE.Sprite(
    new THREE.SpriteMaterial({ map: texture, transparent: true, depthTest: false })
  );
  sprite.position.set(wx, wy, 0.1);
  const worldW = canvas.width / pxScale;
  const worldH = canvas.height / pxScale;
  sprite.scale.set(worldW, worldH, 1);
  return sprite;
}

// ─── Energy Room Header (Three.js — for preview + document) ──

/**
 * Draw a header band above the current scene content.
 * Computes bbox of sceneRoot, draws a rectangle + centered title above it.
 * @param {THREE.Object3D} parent
 * @param {THREE.Object3D} sceneRoot - used to compute bounding box
 * @param {number} ds - drawing scale
 * @param {string} [title='ENERJİ ODASI']
 */
export function drawEnergyRoomHeader(parent, sceneRoot, ds, title = 'ENERJİ ODASI') {
  const box = new THREE.Box3().setFromObject(sceneRoot);
  if (box.isEmpty()) return null;

  const headerH = 50 / ds;
  const minX = box.min.x;
  const maxX = box.max.x;
  const bottomY = box.max.y;
  const topY = bottomY + headerH;

  const group = new THREE.Group();
  group.userData.isHeader = true;

  const color = 0x4287f5;
  group.add(createLineWorld(minX, bottomY, maxX, bottomY, color));
  group.add(createLineWorld(maxX, bottomY, maxX, topY, color));
  group.add(createLineWorld(maxX, topY, minX, topY, color));
  group.add(createLineWorld(minX, topY, minX, bottomY, color));

  const textSize = headerH * 0.5;
  const cx = (minX + maxX) / 2;
  const cy = (bottomY + topY) / 2;
  group.add(createDimensionLabel(title, cx, cy, textSize, '#4287f5'));

  parent.add(group);
  return group;
}

// ─── Document Frame ─────────────────────────────────────

/**
 * Draw a rectangular frame around the entire scene content.
 * @param {THREE.Object3D} parent - The scene or group to add the frame to
 * @param {THREE.Object3D} sceneRoot - The scene to compute bounding box from
 * @param {{top:number, bottom:number, left:number, right:number}} margin - Margins in cm
 * @param {number} ds - Drawing scale
 */
export function drawDocumentFrame(parent, sceneRoot, margin, ds) {
  const box = new THREE.Box3().setFromObject(sceneRoot);
  if (box.isEmpty()) return null;

  const minX = box.min.x - margin.left / ds;
  const minY = box.min.y - margin.bottom / ds;
  const maxX = box.max.x + margin.right / ds;
  const maxY = box.max.y + margin.top / ds;

  const color = 0x888888;
  const group = new THREE.Group();
  group.userData.isDocumentFrame = true;
  group.add(createLineWorld(minX, minY, maxX, minY, color));
  group.add(createLineWorld(maxX, minY, maxX, maxY, color));
  group.add(createLineWorld(maxX, maxY, minX, maxY, color));
  group.add(createLineWorld(minX, maxY, minX, minY, color));

  parent.add(group);
  return group;
}

// ─── Clipped Room ──────────────────────────────────────

export function drawClippedRoom(parent, clippedSegments) {
  const group = new THREE.Group();
  group.userData.isEnergyRoom = true;
  for (const seg of clippedSegments) {
    group.add(createLineWorld(seg.x1, seg.y1, seg.x2, seg.y2, seg.color));
  }
  parent.add(group);
  return group;
}

// ─── Panel Compartment Drawing (CENTER-BASED + ROTATION) ──
// Panel data:
//   { id, label, compartments: [{name, widthCm}], totalWidthCm, depthCm, x, y, rotation }
//   x, y = panel CENTER in world coords
//   rotation = radians
//
// Local coordinate layout:
//          +Y (dimension side, away from wall)
//    ┌──────────────────────┐  +halfD
//    │  Dagitim  │  Sayac   │
//    └──────────────────────┘  -halfD (wall side)
//  -halfW                  +halfW
//                            +X (depth dimension side)

/**
 * @param {number} ds - drawingScale (e.g., 50 for 1:50)
 */
export function drawPanelRect(parent, panel, selected, ds, intersects = false) {
  const group = new THREE.Group();
  group.userData.panelId = panel.id;

  // Position and rotate the group
  group.position.set(panel.x, panel.y, 0);
  group.rotation.z = panel.rotation || 0;

  const color = intersects ? COLORS.panelIntersect : (selected ? COLORS.panelSelected : COLORS.panel);
  const textColor = intersects ? COLORS.compartmentTextIntersect : COLORS.compartmentText;
  const halfW = (panel.totalWidthCm / ds) / 2;
  const halfD = (panel.depthCm / ds) / 2;

  // Outer rectangle in local coords (centered at origin)
  group.add(createLineLocal(-halfW, -halfD, halfW, -halfD, color));
  group.add(createLineLocal(halfW, -halfD, halfW, halfD, color));
  group.add(createLineLocal(halfW, halfD, -halfW, halfD, color));
  group.add(createLineLocal(-halfW, halfD, -halfW, -halfD, color));

  // Compartment dividers + labels (local coords)
  let cx = -halfW;
  for (let i = 0; i < panel.compartments.length; i++) {
    const comp = panel.compartments[i];
    const cw = comp.widthCm / ds;

    if (i > 0) {
      group.add(createLineLocal(cx, -halfD, cx, halfD, color));
    }

    // Label inside compartment (single sprite for all lines — rotation-safe)
    const labelLines = comp.name.split('\n');
    const textSize = (halfD * 2) * 0.22;
    if (labelLines.length === 1) {
      group.add(createDimensionLabel(comp.name, cx + cw / 2, 0, textSize, textColor));
    } else {
      group.add(createMultiLineTextSprite(labelLines, cx + cw / 2, 0, textSize, textColor));
    }

    cx += cw;
  }

  parent.add(group);
  return group;
}

// ─── Dimension Lines ───────────────────────────────────
// drawDimH / drawDimV work in local coords, added to a rotated sub-group.

/**
 * Horizontal dimension with arrows — all positions in local coords.
 */
function drawDimH(group, wx1, wx2, wy, offsetW, label, ds, dir, cfg) {
  const arrowL = cfg.arrowSize / ds;
  const arrowW = (cfg.arrowSize / 3) / ds;
  const extGap = 1.5 / ds;
  const extOver = 1 / ds;
  const ly = wy + dir * offsetW;

  group.add(createLineLocal(wx1, wy + dir * extGap, wx1, ly + dir * extOver, COLORS.dimension));
  group.add(createLineLocal(wx2, wy + dir * extGap, wx2, ly + dir * extOver, COLORS.dimension));
  group.add(createLineLocal(wx1, ly, wx2, ly, COLORS.dimension));
  // Left arrow
  group.add(createLineLocal(wx1, ly, wx1 + arrowL, ly + arrowW, COLORS.dimension));
  group.add(createLineLocal(wx1, ly, wx1 + arrowL, ly - arrowW, COLORS.dimension));
  // Right arrow
  group.add(createLineLocal(wx2, ly, wx2 - arrowL, ly + arrowW, COLORS.dimension));
  group.add(createLineLocal(wx2, ly, wx2 - arrowL, ly - arrowW, COLORS.dimension));
  // Text
  const textSize = cfg.textSize / ds;
  const textOffsetY = (cfg.textSize / 2) / ds;
  group.add(createDimensionLabel(label, (wx1 + wx2) / 2, ly + dir * textOffsetY, textSize, COLORS.dimText));
}

/**
 * Vertical dimension with arrows — all positions in local coords.
 * @param {number} dir - 1 = right, -1 = left
 */
function drawDimV(group, wy1, wy2, wx, offsetW, label, ds, dir, cfg) {
  const arrowL = cfg.arrowSize / ds;
  const arrowW = (cfg.arrowSize / 3) / ds;
  const extGap = 1.5 / ds;
  const extOver = 1 / ds;
  const lx = wx + dir * offsetW;

  group.add(createLineLocal(wx + dir * extGap, wy1, lx + dir * extOver, wy1, COLORS.dimension));
  group.add(createLineLocal(wx + dir * extGap, wy2, lx + dir * extOver, wy2, COLORS.dimension));
  group.add(createLineLocal(lx, wy1, lx, wy2, COLORS.dimension));
  // Bottom arrow
  group.add(createLineLocal(lx, wy1, lx + arrowW, wy1 + arrowL, COLORS.dimension));
  group.add(createLineLocal(lx, wy1, lx - arrowW, wy1 + arrowL, COLORS.dimension));
  // Top arrow
  group.add(createLineLocal(lx, wy2, lx + arrowW, wy2 - arrowL, COLORS.dimension));
  group.add(createLineLocal(lx, wy2, lx - arrowW, wy2 - arrowL, COLORS.dimension));
  // Text
  const textSize = cfg.textSize / ds;
  const textOffsetX = (cfg.textSize / 2) / ds;
  group.add(createDimensionLabel(label, lx + dir * textOffsetX, (wy1 + wy2) / 2, textSize, COLORS.dimText));
}

/**
 * Draw dimension lines for each panel — each in its own rotated sub-group.
 * @param {number} ds - drawingScale
 * @param {object} cfg - { textSize, arrowSize, extInner, extOuter, lineSpacing } in cm
 */
export function drawPanelDimensions(parent, panels, ds, cfg) {
  const group = new THREE.Group();
  group.userData.isDimensions = true;

  for (const panel of panels) {
    const halfW = (panel.totalWidthCm / ds) / 2;
    const halfD = (panel.depthCm / ds) / 2;

    // Create a rotated sub-group positioned at panel center
    const sub = new THREE.Group();
    sub.position.set(panel.x, panel.y, 0);
    sub.rotation.z = panel.rotation || 0;

    // Width dimensions along +Y edge (away from wall)
    let cx = -halfW;
    const innerOffset = cfg.extInner / ds;
    for (let i = 0; i < panel.compartments.length; i++) {
      const comp = panel.compartments[i];
      const cw = comp.widthCm / ds;
      const label = `${comp.widthCm.toFixed(0)} cm`;

      drawDimH(sub, cx, cx + cw, halfD, innerOffset, label, ds, 1, cfg);
      cx += cw;
    }

    // Outer dimension — total width (along +Y edge)
    if (panel.compartments.length > 1) {
      const label = `${panel.totalWidthCm.toFixed(0)} cm`;
      const outerOffset = cfg.extOuter / ds;

      drawDimH(sub, -halfW, halfW, halfD, outerOffset, label, ds, 1, cfg);
    }

    // Depth dimension — to the right (+X edge)
    const depthLabel = `${panel.depthCm.toFixed(0)} cm`;
    const depthOffset = cfg.extInner / ds;
    drawDimV(sub, -halfD, halfD, halfW, depthOffset, depthLabel, ds, 1, cfg);

    group.add(sub);
  }

  parent.add(group);
  return group;
}

// ─── Custom Dimension Lines (world coords) ──────────

const COLORS_CUSTOM_DIM = 0x66bb6a;   // green for user dimensions
const COLORS_CUSTOM_DIM_TEXT = '#66bb6a';

/**
 * Draw a single world-space dimension line between two points.
 * @param {number|undefined} offsetOverride - explicit offset in world coords (signed).
 *   If undefined, uses cfg.extInner / ds.
 * @param {number|undefined} colorOverride - line color override
 * @param {string|undefined} axis - 'h' or 'v' for projected (axis-aligned) dimension.
 *   When set, P1 and P2 can be on different parallel lines. Extension lines will
 *   have different lengths accordingly. Measurement is projected onto the axis.
 */
function drawWorldDimLine(group, x1, y1, x2, y2, label, ds, cfg, offsetOverride, colorOverride, axis) {
  const arrowL = cfg.arrowSize / ds;
  const arrowW = (cfg.arrowSize / 3) / ds;
  const offsetDist = offsetOverride !== undefined ? offsetOverride : (cfg.extInner / ds);
  const extOver = 1 / ds;
  const textSize = cfg.textSize / ds;

  const color = colorOverride !== undefined ? colorOverride : COLORS_CUSTOM_DIM;
  const textColor = colorOverride !== undefined
    ? '#' + (colorOverride & 0xFFFFFF).toString(16).padStart(6, '0')
    : COLORS_CUSTOM_DIM_TEXT;

  const sign = offsetDist >= 0 ? 1 : -1;

  // ── Projected (axis-aligned) dimension ──
  if (axis === 'h' || axis === 'v') {
    const isH = axis === 'h';
    // Reference = midpoint of the two perpendicular coordinates
    const ref = isH ? (y1 + y2) / 2 : (x1 + x2) / 2;
    // Dimension line position
    const dimPos = ref + offsetDist;

    if (isH) {
      const measLen = Math.abs(x2 - x1);
      if (measLen < 1e-6) return;
      // Extension lines (vertical from actual points to dim line + overshoot)
      group.add(createLineWorld(x1, y1, x1, dimPos + sign * extOver, color));
      group.add(createLineWorld(x2, y2, x2, dimPos + sign * extOver, color));
      // Main dimension line (horizontal)
      group.add(createLineWorld(x1, dimPos, x2, dimPos, color));
      // Arrows
      const dir = x2 >= x1 ? 1 : -1;
      group.add(createLineWorld(x1, dimPos, x1 + dir * arrowL, dimPos + arrowW, color));
      group.add(createLineWorld(x1, dimPos, x1 + dir * arrowL, dimPos - arrowW, color));
      group.add(createLineWorld(x2, dimPos, x2 - dir * arrowL, dimPos + arrowW, color));
      group.add(createLineWorld(x2, dimPos, x2 - dir * arrowL, dimPos - arrowW, color));
      // Text
      const tOff = cfg.textSize / ds;
      group.add(createDimensionLabel(label, (x1 + x2) / 2, dimPos + sign * tOff, textSize, textColor));
    } else {
      const measLen = Math.abs(y2 - y1);
      if (measLen < 1e-6) return;
      // Extension lines (horizontal from actual points to dim line + overshoot)
      group.add(createLineWorld(x1, y1, dimPos + sign * extOver, y1, color));
      group.add(createLineWorld(x2, y2, dimPos + sign * extOver, y2, color));
      // Main dimension line (vertical)
      group.add(createLineWorld(dimPos, y1, dimPos, y2, color));
      // Arrows
      const dir = y2 >= y1 ? 1 : -1;
      group.add(createLineWorld(dimPos, y1, dimPos + arrowW, y1 + dir * arrowL, color));
      group.add(createLineWorld(dimPos, y1, dimPos - arrowW, y1 + dir * arrowL, color));
      group.add(createLineWorld(dimPos, y2, dimPos + arrowW, y2 - dir * arrowL, color));
      group.add(createLineWorld(dimPos, y2, dimPos - arrowW, y2 - dir * arrowL, color));
      // Text
      const tOff = cfg.textSize / ds;
      group.add(createDimensionLabel(label, dimPos + sign * tOff, (y1 + y2) / 2, textSize, textColor));
    }
    return;
  }

  // ── Free dimension (along P1→P2 direction) ──
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len = Math.hypot(dx, dy);
  if (len < 1e-6) return;

  // Direction unit vectors
  const ux = dx / len;
  const uy = dy / len;
  // Perpendicular (offset direction for extension lines)
  const px = -uy;
  const py = ux;

  // Offset the dimension line perpendicular to the measurement direction
  const ox1 = x1 + px * offsetDist;
  const oy1 = y1 + py * offsetDist;
  const ox2 = x2 + px * offsetDist;
  const oy2 = y2 + py * offsetDist;

  // Extension lines from points to beyond offset line
  group.add(createLineWorld(x1, y1, x1 + px * (offsetDist + sign * extOver), y1 + py * (offsetDist + sign * extOver), color));
  group.add(createLineWorld(x2, y2, x2 + px * (offsetDist + sign * extOver), y2 + py * (offsetDist + sign * extOver), color));

  // Main dimension line
  group.add(createLineWorld(ox1, oy1, ox2, oy2, color));

  // Arrows at start
  group.add(createLineWorld(ox1, oy1, ox1 + ux * arrowL + px * arrowW, oy1 + uy * arrowL + py * arrowW, color));
  group.add(createLineWorld(ox1, oy1, ox1 + ux * arrowL - px * arrowW, oy1 + uy * arrowL - py * arrowW, color));
  // Arrows at end
  group.add(createLineWorld(ox2, oy2, ox2 - ux * arrowL + px * arrowW, oy2 - uy * arrowL + py * arrowW, color));
  group.add(createLineWorld(ox2, oy2, ox2 - ux * arrowL - px * arrowW, oy2 - uy * arrowL - py * arrowW, color));

  // Text at midpoint, offset further away from points
  const textOffsetDist = cfg.textSize / ds;
  const mx = (ox1 + ox2) / 2 + px * sign * textOffsetDist;
  const my = (oy1 + oy2) / 2 + py * sign * textOffsetDist;
  group.add(createDimensionLabel(label, mx, my, textSize, textColor));
}

/**
 * Draw all custom (user-added) dimension lines.
 * @param {THREE.Object3D} parent
 * @param {Array<{id, x1, y1, x2, y2, offset}>} customDimensions - world coords
 * @param {number} ds - drawingScale
 * @param {object} cfg - dimension settings in cm
 */
export function drawCustomDimensions(parent, customDimensions, ds, cfg) {
  if (!customDimensions || customDimensions.length === 0) return null;
  const group = new THREE.Group();
  group.userData.isCustomDimensions = true;

  for (const dim of customDimensions) {
    const dx = dim.x2 - dim.x1;
    const dy = dim.y2 - dim.y1;
    // For projected dimensions, measure along the axis only
    const distCm = dim.axis === 'h' ? Math.abs(dx) * ds
      : dim.axis === 'v' ? Math.abs(dy) * ds
      : Math.hypot(dx, dy) * ds;
    const label = `${distCm.toFixed(1)} cm`;
    drawWorldDimLine(group, dim.x1, dim.y1, dim.x2, dim.y2, label, ds, cfg, dim.offset, undefined, dim.axis);
  }

  parent.add(group);
  return group;
}

/**
 * Draw a marker at the first clicked point (small cross).
 */
export function drawDimFirstPointMarker(parent, point) {
  if (!point) return null;
  const group = new THREE.Group();
  const s = 0.8; // marker size in world
  const color = COLORS_CUSTOM_DIM;
  group.add(createLineWorld(point.x - s, point.y, point.x + s, point.y, color));
  group.add(createLineWorld(point.x, point.y - s, point.x, point.y + s, color));

  // Small circle via line segments
  const segs = 16;
  const r = s * 0.7;
  for (let i = 0; i < segs; i++) {
    const a1 = (i / segs) * Math.PI * 2;
    const a2 = ((i + 1) / segs) * Math.PI * 2;
    group.add(createLineWorld(
      point.x + Math.cos(a1) * r, point.y + Math.sin(a1) * r,
      point.x + Math.cos(a2) * r, point.y + Math.sin(a2) * r,
      color
    ));
  }

  parent.add(group);
  return group;
}

/**
 * Draw dimension preview during creation.
 *
 * @param {'pickSecond'|'pickOffset'} phase
 * @param {{x,y}} p1 - first point (confirmed)
 * @param {{x,y}|null} p2 - second point (confirmed in pickOffset phase)
 * @param {{x,y}} preview - current mouse position (world)
 * @param {number} ds - drawingScale
 * @param {object} cfg - dimension settings in cm
 * @param {string|undefined} axis - 'h' or 'v' for projected dimension (pickOffset phase)
 */
export function drawDimPreview(parent, phase, p1, p2, preview, ds, cfg, axis) {
  if (!p1 || !preview) return null;
  const group = new THREE.Group();
  const previewColor = 0x66bb6a;

  if (phase === 'pickSecond') {
    // Determine preview axis from P1 to preview point
    const dx = preview.x - p1.x;
    const dy = preview.y - p1.y;
    const adx = Math.abs(dx);
    const ady = Math.abs(dy);
    if (adx < 1e-6 && ady < 1e-6) { parent.add(group); return group; }

    const previewAxis = adx >= ady ? 'h' : 'v';
    const projDist = previewAxis === 'h' ? adx : ady;
    const distCm = projDist * ds;
    const label = `${distCm.toFixed(1)} cm`;
    const textSize = cfg.textSize / ds;
    const tOff = cfg.textSize / ds;

    if (previewAxis === 'h') {
      // Horizontal measurement preview
      const yMid = (p1.y + preview.y) / 2;
      // Construction lines (vertical from actual points to midline)
      group.add(createLineWorld(p1.x, p1.y, p1.x, yMid, previewColor));
      group.add(createLineWorld(preview.x, preview.y, preview.x, yMid, previewColor));
      // Measurement line (horizontal)
      group.add(createLineWorld(p1.x, yMid, preview.x, yMid, previewColor));
      // Text
      group.add(createDimensionLabel(label, (p1.x + preview.x) / 2, yMid + tOff, textSize, COLORS_CUSTOM_DIM_TEXT));
    } else {
      // Vertical measurement preview
      const xMid = (p1.x + preview.x) / 2;
      // Construction lines (horizontal from actual points to midline)
      group.add(createLineWorld(p1.x, p1.y, xMid, p1.y, previewColor));
      group.add(createLineWorld(preview.x, preview.y, xMid, preview.y, previewColor));
      // Measurement line (vertical)
      group.add(createLineWorld(xMid, p1.y, xMid, preview.y, previewColor));
      // Text
      group.add(createDimensionLabel(label, xMid + tOff, (p1.y + preview.y) / 2, textSize, COLORS_CUSTOM_DIM_TEXT));
    }
  } else if (phase === 'pickOffset' && p2) {
    // Show full projected dimension with dynamic offset from mouse
    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;

    if (axis === 'h' || axis === 'v') {
      const isH = axis === 'h';
      const projDist = isH ? Math.abs(dx) : Math.abs(dy);
      if (projDist < 1e-6) { parent.add(group); return group; }
      const distCm = projDist * ds;
      const label = `${distCm.toFixed(1)} cm`;
      // Compute projected offset
      const ref = isH ? (p1.y + p2.y) / 2 : (p1.x + p2.x) / 2;
      const offset = (isH ? preview.y : preview.x) - ref;
      drawWorldDimLine(group, p1.x, p1.y, p2.x, p2.y, label, ds, cfg, offset, previewColor, axis);
    } else {
      // Fallback: free dimension (legacy)
      const len = Math.hypot(dx, dy);
      if (len > 1e-6) {
        const offset = (dx * (preview.y - p1.y) - dy * (preview.x - p1.x)) / len;
        const distCm = len * ds;
        const label = `${distCm.toFixed(1)} cm`;
        drawWorldDimLine(group, p1.x, p1.y, p2.x, p2.y, label, ds, cfg, offset, previewColor);
      }
    }
  }

  parent.add(group);
  return group;
}

/**
 * Draw a snap type indicator at the active snap point.
 * - endpoint: square (□)
 * - midpoint: triangle (△)
 * - panelCorner: diamond (◇)
 * - nearest: perpendicular mark (⊥)
 * - free: nothing
 *
 * @param {THREE.Object3D} parent
 * @param {{x: number, y: number, type: string}} point
 * @param {number} ds - drawing scale (so indicator size is consistent in cm)
 */
export function drawSnapIndicator(parent, point, ds = 1) {
  if (!point || point.type === 'free') return null;

  const group = new THREE.Group();
  const s = 8 / ds; // ~8 cm regardless of drawing scale
  const color = 0x00ff66; // bright green

  if (point.type === 'endpoint') {
    // Square (□)
    group.add(createLineWorld(point.x - s, point.y - s, point.x + s, point.y - s, color));
    group.add(createLineWorld(point.x + s, point.y - s, point.x + s, point.y + s, color));
    group.add(createLineWorld(point.x + s, point.y + s, point.x - s, point.y + s, color));
    group.add(createLineWorld(point.x - s, point.y + s, point.x - s, point.y - s, color));
  } else if (point.type === 'midpoint') {
    // Triangle (△)
    group.add(createLineWorld(point.x, point.y + s, point.x - s, point.y - s, color));
    group.add(createLineWorld(point.x - s, point.y - s, point.x + s, point.y - s, color));
    group.add(createLineWorld(point.x + s, point.y - s, point.x, point.y + s, color));
  } else if (point.type === 'panelCorner') {
    // Diamond (◇)
    group.add(createLineWorld(point.x, point.y + s, point.x + s, point.y, color));
    group.add(createLineWorld(point.x + s, point.y, point.x, point.y - s, color));
    group.add(createLineWorld(point.x, point.y - s, point.x - s, point.y, color));
    group.add(createLineWorld(point.x - s, point.y, point.x, point.y + s, color));
  } else if (point.type === 'nearest') {
    // Perpendicular mark (⊥) — small cross with a horizontal base
    const hs = s * 0.7;
    group.add(createLineWorld(point.x, point.y - hs, point.x, point.y + hs, color));
    group.add(createLineWorld(point.x - hs, point.y - hs, point.x + hs, point.y - hs, color));
  }

  parent.add(group);
  return group;
}

/**
 * Draw small markers at all wall corner points (to show snap targets).
 */
export function drawCornerMarkers(parent, corners) {
  if (!corners || corners.length === 0) return null;
  const group = new THREE.Group();
  const s = 0.5;
  const color = 0xffab40; // orange for corner markers

  for (const c of corners) {
    // Small diamond
    group.add(createLineWorld(c.x, c.y - s, c.x + s, c.y, color));
    group.add(createLineWorld(c.x + s, c.y, c.x, c.y + s, color));
    group.add(createLineWorld(c.x, c.y + s, c.x - s, c.y, color));
    group.add(createLineWorld(c.x - s, c.y, c.x, c.y - s, color));
  }

  parent.add(group);
  return group;
}
